import { gettext } from '@eventbrite/i18n';
import type { CompactCalendarCellProps } from '@eventbrite/timed-entry';
import type { Moment } from 'moment';
import momentTimezone from 'moment-timezone';
import React from 'react';
import {
    DateEventStatus,
    Discount,
    DiscountType,
    type CompactCalendarData,
    type EventDate,
    type ScrollCalendarData,
} from '../../../state/infiniteCalendarSeries';
import { DiscountSvg } from '../assests/DiscountSvg';
import defaultStyles from './CompactCalendar.module.scss';
import rebrandingStyles from './CompactCalendar.rebranding.module.scss';

import classNames from 'classnames';
import {
    DEFAULT_STYLES,
    getStyles,
    REBRANDING_STYLES,
} from '../../../utils/rebrand';
import { DATE_FORMATS } from '../../InfiniteCalendarSeries/constants';
import { getFormattedDate } from '../../InfiniteCalendarSeries/utils';
import { COLORS } from '../colorConstants';

const stylesModules = {
    [DEFAULT_STYLES]: defaultStyles,
    [REBRANDING_STYLES]: rebrandingStyles,
};

export const hasValidDiscount = (discount?: Discount): boolean => {
    if (!discount) {
        return false;
    }

    return (
        (discount.type === DiscountType.percentage &&
            discount.percentage > 0) ||
        (discount.type === DiscountType.fixed && discount.amount > 0)
    );
};

export function _getDateCellKey(
    eventDate: EventDate,
    today: Moment,
    timezone: string,
): DateEventStatus {
    const eventDay = momentTimezone.tz(eventDate.date, timezone).startOf('day');

    if (eventDay.isBefore(today)) {
        return DateEventStatus.unavailable;
    }
    switch (true) {
        case hasValidDiscount(eventDate.discount):
            return DateEventStatus.discount;
        case eventDate.checkoutEnabled && eventDate.highDemand:
            return DateEventStatus.highDemand;
        case eventDate.checkoutEnabled:
            return DateEventStatus.available;
        case eventDate.soldOut:
            return DateEventStatus.soldOut;
        default:
            return DateEventStatus.unavailable;
    }
}

export function mapEventDatesToScrollCalendarData({
    eventDates,
    previousScrollCalendarData,
    month,
    year,
    timezone,
}: {
    eventDates: EventDate[];
    previousScrollCalendarData: ScrollCalendarData;
    month: number;
    year: number;
    timezone: string;
}) {
    const today = momentTimezone.tz(new Date(), timezone).startOf('day');
    const calendarData: ScrollCalendarData = previousScrollCalendarData || {
        years: {},
    };

    calendarData.years ||= {};

    for (const eventDate of eventDates) {
        const date = getFormattedDate({
            isoDate: eventDate.date,
            timezone,
        }).date;

        const year = date.getFullYear();
        const month = date.getMonth();
        calendarData.years[year] ??= { months: {} };
        calendarData.years[year].months[month] ??= {
            dates: {},
        };

        calendarData.years[year].months[month].dates[date.getDate()] ||= {
            dateCellKey: _getDateCellKey(eventDate, today, timezone),
            dateString: eventDate.date,
            sessions: [],
        };
    }

    calendarData.years[year].months[month].isLoading = false;

    return calendarData;
}

export const _getEventDatesByMonth = (
    eventData: EventDate[],
    locale: string,
    timezone: string,
) =>
    eventData.reduce((acc, event) => {
        const month = getFormattedDate({
            isoDate: event.date,
            timezone,
        }).format(locale, DATE_FORMATS.MONTH_ONLY);

        acc[month] = [...(acc[month] || []), event];
        return acc;
    }, {} as Record<string, EventDate[]>);

export const _getCellClassName = (
    event: EventDate,
    isAppRebrandActive: boolean,
) => {
    const theme = getStyles(isAppRebrandActive);
    const styles = stylesModules[theme];
    const hasDiscount = hasValidDiscount(event.discount);
    return classNames(styles.compactCalendarContainer, styles.dateCell, {
        [styles.highDemandDateCell]: !hasDiscount && event.highDemand,
        [styles.soldOutDateCell]: event.soldOut,
        [styles.discountDateCell]: hasDiscount,
    });
};

export function _getCellProps(event: EventDate, isAppRebrandActive: boolean) {
    const cellProps: CompactCalendarCellProps['cellProps'] = {
        isDisabled: false,
        icons: [],
    };
    const className = _getCellClassName(event, isAppRebrandActive);
    if (event.soldOut) {
        cellProps.isDisabled = false;
        cellProps.weekdayText = gettext('Sold out').toString();
    }
    if (hasValidDiscount(event.discount)) {
        cellProps?.icons?.push(
            <DiscountSvg
                fill={
                    isAppRebrandActive
                        ? COLORS['blue-300']
                        : COLORS['orange-500']
                }
                width={'16'}
                height={'16'}
            />,
        );
    }
    cellProps.className = className;
    return cellProps;
}

export function mapEventDatesToCompactCalendarData(
    eventData: EventDate[],
    timezone: string,
    locale: string,
    isAppRebrandActive: boolean,
) {
    const calendarData: CompactCalendarData = [];
    const eventDatesByMonth = _getEventDatesByMonth(
        eventData,
        locale,
        timezone,
    );
    for (const monthName in eventDatesByMonth) {
        const events = eventDatesByMonth[monthName];
        const dateCells: CompactCalendarCellProps[] = events.map((event) => {
            const date = getFormattedDate({
                isoDate: event.date ?? event.sessionStartDatetime,
                timezone,
            }).date;

            const props: CompactCalendarCellProps = {
                cellProps: _getCellProps(event, isAppRebrandActive),
                date: event.date,
                id: event.sessionId as string, // as string - we will get sessionId for the compact calendar
                sessionTime: event.sessionStartDatetime,
                cellDate: date,
                timezone,
                formattedSessionTime:
                    event.sessionStartDatetime &&
                    getFormattedDate({
                        isoDate: event.sessionStartDatetime,
                        timezone,
                    }).format(locale, DATE_FORMATS.TIME_ONLY),

                formattedDate: date.getDate(),
            };
            return props;
        });
        calendarData.push({
            monthLabel: monthName,
            dateCells,
        });
    }

    return calendarData;
}

export function trimISODatetimeToDate(dateString: string): string {
    return dateString.slice(0, 10);
}
